import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Plus, User, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

const RoomViewCard = ({ room, roomType, reservation, onAddReservation, onViewReservation, canChangeRoomStatus, onStatusChange }) => {
  const isOccupied = reservation && reservation.status === 'IN_HOUSE';
  const isReserved = reservation && reservation.status === 'CONFIRMED';
  
  const getStatusColor = () => {
    if (room.status === 'OOO') return 'bg-orange-100 border-orange-300';
    if (isOccupied) return 'bg-emerald-100 border-emerald-300';
    if (isReserved) return 'bg-blue-100 border-blue-300';
    if (room.status === 'DIRTY') return 'bg-red-100 border-red-300';
    return 'bg-white border-slate-200';
  };

  const getRoomStatusColor = (status) => {
    const colors = {
      CLEAN: 'bg-emerald-500',
      DIRTY: 'bg-red-500',
      IN_PROGRESS: 'bg-amber-500',
      INSPECTED: 'bg-blue-500',
      OCCUPIED: 'bg-purple-500',
      OOO: 'bg-slate-400'
    };
    return colors[status] || 'bg-slate-400';
  };

  const getStatusBadge = () => {
    if (room.status === 'OOO') return <Badge variant="destructive">Out of Order</Badge>;
    if (isOccupied) return <Badge className="bg-emerald-600">Occupied</Badge>;
    if (isReserved) return <Badge className="bg-blue-600">Reserved</Badge>;
    if (room.status === 'DIRTY') return <Badge variant="outline" className="text-red-600">Dirty</Badge>;
    return <Badge variant="outline" className="bg-green-50 text-green-700">Available</Badge>;
  };

  return (
    <Card className={`${getStatusColor()} border-2 transition-all hover:shadow-md`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>{room.name}</span>
            {room.smoking_policy === 'SMOKING' && (
              <span title="Smoking Allowed" className="text-xl">🚬</span>
            )}
            {room.smoking_policy === 'NON_SMOKING' && (
              <span title="Non-Smoking" className="text-xl">🚭</span>
            )}
          </div>
          {getStatusBadge()}
        </CardTitle>
        <p className="text-sm text-slate-600">{roomType?.name || 'Unknown Type'}</p>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Room Status with Dropdown */}
        <div className="flex items-center justify-between pb-2 border-b border-slate-200">
          <span className="text-xs font-medium text-slate-600">Room Status:</span>
          {canChangeRoomStatus ? (
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 cursor-pointer focus:outline-none text-xs">
                  <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(room.status)}`}></span>
                  <span className="font-medium underline">{room.status}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-2" align="start">
                <div className="space-y-1">
                  {['CLEAN', 'DIRTY', 'IN_PROGRESS', 'INSPECTED', 'OOO'].map((status) => (
                    <button
                      key={status}
                      onClick={() => onStatusChange && onStatusChange(room.id, status)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 rounded flex items-center gap-2 transition"
                    >
                      <span className={`w-2 h-2 rounded-full ${getRoomStatusColor(status)}`}></span>
                      {status}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="flex items-center gap-1 text-xs">
              <span className={`inline-block w-2 h-2 rounded-full ${getRoomStatusColor(room.status)}`}></span>
              <span className="font-medium">{room.status}</span>
            </div>
          )}
        </div>

        {/* OOO Info */}
        {room.status === 'OOO' && room.maintenance_note && (
          <div className="bg-orange-50 p-2 rounded text-sm">
            <p className="font-medium text-orange-900">Reason:</p>
            <p className="text-orange-800">{room.maintenance_note}</p>
            {room.maintenance_end && (
              <p className="text-xs text-orange-700 mt-1">
                Expected return: {format(new Date(room.maintenance_end), 'MMM dd, h:mm a')}
              </p>
            )}
          </div>
        )}

        {/* Occupied - Show Guest */}
        {isOccupied && reservation && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User size={14} className="text-slate-500" />
              <span className="font-medium text-slate-900">{reservation.guest?.name || 'Guest'}</span>
            </div>
            <div className="text-sm text-slate-600">
              <p>Check-in: {format(new Date(reservation.check_in + 'T00:00:00'), 'MMM dd')}</p>
              <p>Check-out: {format(new Date(reservation.check_out + 'T00:00:00'), 'MMM dd')}</p>
              {reservation.check_out && (
                <p className="text-xs mt-1">
                  {Math.ceil((new Date(reservation.check_out + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                </p>
              )}
            </div>
            {reservation.base_amount && (
              <div className="flex items-center gap-2 text-sm">
                <DollarSign size={14} className="text-slate-500" />
                <span className="font-medium">
                  ${reservation.base_amount}
                  {reservation.stay_type === 'weekly' ? '/week' : 
                   reservation.stay_type === 'day_use' ? '/day' :
                   ['1hr','2hr','6hr','12hr'].includes(reservation.stay_type) ? ' total' :
                   '/night'}
                </span>
              </div>
            )}
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full"
              onClick={() => onViewReservation && onViewReservation(reservation)}
            >
              View Details
            </Button>
          </div>
        )}

        {/* Reserved - Show Preview */}
        {isReserved && reservation && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-blue-900">
              Arriving: {format(new Date(reservation.check_in + 'T00:00:00'), 'MMM dd')}
            </p>
            <p className="text-sm text-slate-600">
              Departing: {format(new Date(reservation.check_out + 'T00:00:00'), 'MMM dd')}
            </p>
            <p className="text-sm text-slate-600">{reservation.guest?.name || 'Guest'}</p>
            <Button 
              size="sm" 
              variant="outline" 
              className="w-full"
              onClick={() => onViewReservation && onViewReservation(reservation)}
            >
              View Reservation
            </Button>
          </div>
        )}

        {/* Available - Quick Add */}
        {!isOccupied && !isReserved && room.status !== 'OOO' && (
          <Button 
            size="sm" 
            variant="outline" 
            className="w-full border-dashed border-2 hover:border-cyan-500 hover:bg-cyan-50"
            onClick={() => onAddReservation && onAddReservation(room)}
          >
            <Plus size={16} className="mr-2" />
            New Reservation
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default RoomViewCard;
